import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, catchError, map, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ErrorMessages } from '@app/shared/constants';

// ==================== Request Interfaces ====================

export interface GetSortScansParams {
  startDate: string; // UTC ISO string
  endDate: string; // UTC ISO string
  localTimezone?: string; // Browser timezone (e.g., 'America/Toronto') for date normalization
  locations?: string[]; // ['YYC', 'YYZ', 'YUL', 'YVR', 'YEG', 'Unknown']
}

export interface ExportSortScansParams {
  startDate: string; // UTC ISO string
  endDate: string; // UTC ISO string
  localTimezone?: string; // Browser timezone for date formatting in CSV headers
  locations?: string[]; // Filter by airport codes
}

export interface GetPickupScansParams {
  startDate: string; // UTC ISO string
  endDate: string; // UTC ISO string
  localTimezone?: string; // Browser timezone (e.g., 'America/Toronto') for date normalization
  locations?: string[]; // Filter by tracking_events.location
  pickupProviders?: string[]; // Filter by tracking_events.pickup_provider
  page?: number; // Page number (1-indexed, default 1)
  limit?: number; // Items per page (default 20, max 100)
}

export interface ExportPickupScansParams {
  startDate: string; // UTC ISO string
  endDate: string; // UTC ISO string
  localTimezone?: string; // Browser timezone for date formatting in CSV headers
  locations?: string[]; // Filter by tracking_events.location
  pickupProviders?: string[]; // Filter by tracking_events.pickup_provider
}

// ==================== Response Interfaces ====================

// Sort Scans Response
export interface SortScansAirportData {
  location: string; // Airport code
  aggregatedData: {
    datetime: string; // UTC ISO date (YYYY-MM-DD)
    outboundSortingScansCount: number;
    inboundSortingScansCount: number; // Mock as 0
  }[];
  totalOutboundSortingScansForLocation: number; // Total outbound across all dates
  totalInboundSortingScansForLocation: number; // Total inbound across all dates
}

export interface DailyTotalSortScans {
  datetime: string; // UTC ISO date (YYYY-MM-DD)
  totalOutboundSortingScans: number;
  totalInboundSortingScans: number; // Mock as 0
}

export interface SortScansResponse {
  startDate: string;
  endDate: string;
  localTimezone: string; // Browser timezone used for date normalization (for audit/debug)
  availableLocations: string[]; // Dynamic locations from data for filtering
  totalCost: number; // Mock to 0
  totalFirstMileScans: number; // Unique shipments with ST code across entire range
  totalLastMileScans: number; // Mock to 0
  totalScans: number; // = totalFirstMileScans + totalLastMileScans
  data: SortScansAirportData[];
  dailyTotalData: DailyTotalSortScans[];
}

// Pickup Scans Response

/**
 * Location-level breakdown for other pickup scans (ST events).
 * Cost is calculated per location using tiered pricing, then summed.
 */
export interface OtherPickupScansCountDetail {
  location: string; // Airport code (e.g., 'YYZ', 'YVR')
  otherPickupScansCountPerLocation: number; // ST event count for this location
  otherPickupScansCostPerLocation: number; // Cost calculated using tiered pricing for this location's count
}

export interface PickupScansCompanyData {
  companyName: string;
  companyId: number;
  companyUuid: string;
  totalCompanyPickupScans: number;
  aggregatedData: {
    datetime: string; // UTC ISO date (YYYY-MM-DD)
    pickupScansCount: number; // PU events count
    pickupScansCost: number; // pickupScansCount * $0.10
    otherPickupScansCount: number; // Sum of otherPickupScansCountPerLocation across all locations
    otherPickupScansCost: number; // Sum of otherPickupScansCostPerLocation (tiered pricing per location)
    otherPickupScansCountDetails: OtherPickupScansCountDetail[]; // Per-location breakdown for pricing accuracy
    totalScansPerDay: number; // pickupScansCount + otherPickupScansCount
    totalCostPerDay: number; // pickupScansCost + otherPickupScansCost
  }[];
}

export interface DailyTotalPickupScans {
  datetime: string; // UTC ISO date (YYYY-MM-DD)
  combinedTotalDailyPickupScans: number; // totalDailyPickupScans + totalDailyOtherPickupScans
  combinedTotalDailyPickupScansCost: number; // totalDailyPickupScansCost + totalDailyOtherPickupScansCost
  totalDailyPickupScans: number; // sum of pickupScansCount for all companies
  totalDailyPickupScansCost: number; // sum of pickupScansCost for all companies
  totalDailyOtherPickupScans: number; // sum of otherPickupScansCountPerLocation across all locations
  totalDailyOtherPickupScansCost: number; // sum of otherPickupScansCostPerLocation (tiered pricing per location)
  otherPickupScansCountDetails: OtherPickupScansCountDetail[]; // Per-location breakdown for pricing accuracy
}

export interface Pagination {
  page: number; // Current page (1-indexed)
  limit: number; // Items per page
  totalItems: number; // Total companies count
  totalPages: number; // Total pages
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

export interface PaginatedPickupScansResponse {
  startDate: string;
  endDate: string;
  localTimezone: string; // Browser timezone used for date normalization (for audit/debug)
  pickupProviders: string[];
  locations: string[];
  availableLocations: string[]; // Dynamic locations from data for filtering
  totalCost: number; // Mock to 0
  totalPickupScans: number; // Unique shipments with PU code across entire range
  totalScans: number; // = totalPickupScans (same value for pickups)
  pagination: Pagination;
  data: PickupScansCompanyData[]; // Paginated company data
  dailyTotalPickupScans: DailyTotalPickupScans[]; // Totals across ALL companies (not just current page)
  totalDailyTotalPickupScansForAllCompanies: number; // Total pickup scans for all companies across all dates
  totalDailyTotalPickupScansForAllCompaniesCost: number; // Total cost for all companies across all dates
}

@Injectable({ providedIn: 'root' })
export class ScanEventsService {
  constructor(private http: HttpClient) {}

  /**
   * Get sort scan aggregates for the dashboard.
   * Groups ST (sorting) events by location and date.
   */
  public getSortScans(params: GetSortScansParams): Observable<SortScansResponse> {
    const httpParams = this.buildSortScansParams(params);
    return this.http
      .get<SortScansResponse>(`${environment.APP_SERVICES_URL}/admin/scan-events/sorts`, {
        params: httpParams,
      })
      .pipe(
        map((res: SortScansResponse) => res),
        catchError((error: any) => throwError(() => error.error || ErrorMessages.UNEXPECTED))
      );
  }

  /**
   * Export sort scan data to CSV (all locations).
   * Returns a Blob that can be downloaded as a CSV file.
   */
  public exportSortScansCsv(params: ExportSortScansParams): Observable<Blob> {
    const httpParams = this.buildSortScansParams(params);
    return this.http
      .get(`${environment.APP_SERVICES_URL}/admin/scan-events/sorts/export`, {
        params: httpParams,
        responseType: 'blob',
      })
      .pipe(catchError((error: any) => throwError(() => error.error || ErrorMessages.UNEXPECTED)));
  }

  /**
   * Get pickup scan aggregates for the dashboard (paginated).
   * Groups PU (pickup) events by company and date.
   */
  public getPickupScans(params: GetPickupScansParams): Observable<PaginatedPickupScansResponse> {
    const httpParams = this.buildPickupScansParams(params);
    return this.http
      .get<PaginatedPickupScansResponse>(`${environment.APP_SERVICES_URL}/admin/scan-events/pickups`, {
        params: httpParams,
      })
      .pipe(
        map((res: PaginatedPickupScansResponse) => res),
        catchError((error: any) => throwError(() => error.error || ErrorMessages.UNEXPECTED))
      );
  }

  /**
   * Export pickup scan data to CSV (all companies, no pagination).
   * Returns a Blob that can be downloaded as a CSV file.
   */
  public exportPickupScansCsv(params: ExportPickupScansParams): Observable<Blob> {
    const httpParams = this.buildExportParams(params);
    return this.http
      .get(`${environment.APP_SERVICES_URL}/admin/scan-events/pickups/export`, {
        params: httpParams,
        responseType: 'blob',
      })
      .pipe(catchError((error: any) => throwError(() => error.error || ErrorMessages.UNEXPECTED)));
  }

  /**
   * Build HTTP params for sort scans request.
   */
  private buildSortScansParams(params: GetSortScansParams): HttpParams {
    let httpParams = new HttpParams().set('startDate', params.startDate).set('endDate', params.endDate);

    // Add local timezone for date normalization
    if (params.localTimezone) {
      httpParams = httpParams.set('localTimezone', params.localTimezone);
    }

    // Append each location as separate param for array handling
    if (params.locations?.length) {
      params.locations.forEach((loc) => {
        httpParams = httpParams.append('locations', loc);
      });
    }

    return httpParams;
  }

  /**
   * Build HTTP params for pickup scans request.
   */
  private buildPickupScansParams(params: GetPickupScansParams): HttpParams {
    let httpParams = new HttpParams().set('startDate', params.startDate).set('endDate', params.endDate);

    // Add local timezone for date normalization
    if (params.localTimezone) {
      httpParams = httpParams.set('localTimezone', params.localTimezone);
    }

    // Append each location as separate param for array handling
    if (params.locations?.length) {
      params.locations.forEach((loc) => {
        httpParams = httpParams.append('locations', loc);
      });
    }

    // Append each pickup provider as separate param
    if (params.pickupProviders?.length) {
      params.pickupProviders.forEach((provider) => {
        httpParams = httpParams.append('pickupProviders', provider);
      });
    }

    // Pagination params
    if (params.page) {
      httpParams = httpParams.set('page', params.page.toString());
    }
    if (params.limit) {
      httpParams = httpParams.set('limit', params.limit.toString());
    }

    return httpParams;
  }

  /**
   * Build HTTP params for export request (no pagination).
   */
  private buildExportParams(params: ExportPickupScansParams): HttpParams {
    let httpParams = new HttpParams().set('startDate', params.startDate).set('endDate', params.endDate);

    // Add local timezone for date formatting in CSV headers
    if (params.localTimezone) {
      httpParams = httpParams.set('localTimezone', params.localTimezone);
    }

    // Append each location as separate param for array handling
    if (params.locations?.length) {
      params.locations.forEach((loc) => {
        httpParams = httpParams.append('locations', loc);
      });
    }

    // Append each pickup provider as separate param
    if (params.pickupProviders?.length) {
      params.pickupProviders.forEach((provider) => {
        httpParams = httpParams.append('pickupProviders', provider);
      });
    }

    return httpParams;
  }
}
